import React from "react";
import { motion } from "framer-motion";
import { Bug, Landmark, BadgeCheck } from "lucide-react";
import { Container, Section, Eyebrow, DisplayHeading, BodyCopy, StatusBadge, Reveal } from "@/components/kit";
import { fadeUp, stagger, viewport } from "@/lib/motion";

const STAGES = [
  {
    no: "01", title: "Test", icon: Bug, tone: "brand",
    copy: "The agent enters a controlled assessment. Real attack patterns run against it.",
    items: ["Prompt injection", "Data leakage", "Jailbreak resistance", "Insecure output", "Excessive agency", "System prompt exposure"],
    outcome: "2 findings", status: "finding",
  },
  {
    no: "02", title: "Govern", icon: Landmark, tone: "brand",
    copy: "Each finding moves into governance — routed to a risk, a control, an owner and evidence.",
    items: ["Finding", "Risk", "Control", "Owner", "Evidence", "Audit trail"],
    outcome: "Controls mapped", status: "active",
  },
  {
    no: "03", title: "Prove", icon: BadgeCheck, tone: "gold",
    copy: "Evidence consolidates. The Trust Score resolves. A verifiable certificate is issued.",
    items: ["Trust Score 87", "Trust Passport", "Assurance record", "CERT-DEMO-000148"],
    outcome: "Verified", status: "verified",
  },
];

export default function TestGovernProve() {
  return (
    <Section id="test-govern-prove" className="border-t border-line bg-panel-raised/30">
      <Container>
        <Reveal><Eyebrow tone="brand">The Meira method</Eyebrow></Reveal>
        <DisplayHeading className="mt-6 max-w-3xl text-3xl leading-tight sm:text-4xl md:text-5xl">
          Test it. Govern it. <span className="text-gradient">Prove it.</span>
        </DisplayHeading>
        <Reveal i={1}>
          <BodyCopy className="mt-6 max-w-2xl">
            Follow one demonstration AI system end to end — a{" "}
            <span className="text-content">Customer Support Agent</span> in a simulated environment.
            Watch a claim become evidence, and evidence become proof.
          </BodyCopy>
        </Reveal>

        <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewport} className="relative mt-10 flex flex-col sm:mt-14">
          {/* spine */}
          <span className="absolute left-[27px] top-6 hidden h-[calc(100%-3rem)] w-px bg-gradient-to-b from-brand/40 via-line to-valid/40 md:block" />
          {STAGES.map((s, idx) => (
            <motion.div key={s.no} variants={fadeUp} className="relative grid gap-4 py-4 sm:gap-6 sm:py-6 md:grid-cols-[auto_1fr] md:gap-10">
              {/* node + number */}
              <div className="relative z-10 flex items-center gap-5 md:flex-col md:items-center md:gap-3">
                <span className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-full border bg-ink ${s.tone === "gold" ? "border-gold/40" : "border-brand/40"}`}>
                  <s.icon size={22} strokeWidth={1.4} className={s.tone === "gold" ? "text-gold" : "text-brand-soft"} />
                </span>
                <span className="font-display text-5xl font-light text-content/10 md:hidden">{s.no}</span>
              </div>

              {/* content */}
              <div className="group rounded-lg border border-line bg-panel-raised p-5 transition-colors duration-300 hover:border-brand/50 sm:p-7 md:p-8">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-baseline gap-4">
                    <span className="hidden font-display text-2xl font-light text-content/15 md:inline">{s.no}</span>
                    <h3 className={`font-display text-2xl font-medium sm:text-3xl ${s.tone === "gold" ? "text-gradient-gold" : "text-content"}`}>{s.title}</h3>
                  </div>
                  <StatusBadge status={s.status}>{s.outcome}</StatusBadge>
                </div>
                <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-content-muted">{s.copy}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {s.items.map((it) => (
                    <span key={it} className="rounded-full border border-line bg-panel-raised px-3 py-1.5 font-mono text-[11px] tracking-wide text-content-muted transition-colors group-hover:border-brand/50">{it}</span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </Container>
    </Section>
  );
}
